import React, { useCallback, useContext, useEffect, useRef, useState, useMemo } from 'react'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import styled from 'styled-components'
import OfferCalendarMonthPicker from '../CalendarPicker/MonthPicker/OfferCalendarMonthPicker'
import OfferCalendarDayPicker from '../CalendarPicker/DayPicker/OfferCalendarDayPicker'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import { getComplementaryBundleState, getAvailableBundlePackages, BundleOfferPackageOptionView, getBundleCalendarMonths } from 'selectors/offerPage/bundledOfferPageSelectors'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions, PaginatorState, BundledOfferState, OfferPageState, takeCurrentOfferState } from 'contexts/OfferPage/offerPageStateReducer'
import OfferCalendarNotAvailableMessage from '../CalendarPicker/OfferCalendarNotAvailableMessage'
import OfferCalendarNoFlightsMessage from '../CalendarPicker/OfferCalendarNoFlightsMessage'
import { CALENDAR_GA_EVENT_ACTION, CALENDAR_GA_EVENT_LABEL } from 'constants/analytics'
import Heading from 'LuxKit/Typography/Heading'
import OfferCalendarDurationSelect from '../CalendarPicker/CalendarDuration/OfferCalendarDurationSelect'
import useHasMounted from 'hooks/useHasMounted'
import BodyText from 'LuxKit/Typography/BodyText'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import CalendarPickerRecommendations from '../CalendarPicker/Recommendations/CalendarPickerRecommendations'
import { shouldEnableCalendarRecommendations } from 'selectors/featuresSelectors'
import Group from 'components/utils/Group'
import OfferCalendarBundlePackageSelect from '../CalendarPicker/CalendarBundlePackages/OfferCalendarBundlePackageSelect'
import OfferBundleAndSaveCalendarDatePickerDropdownFooter from './OfferBundleAndSaveCalendarDatePickerDropdownFooter'
import { isBundleAndSaveV2Enabled } from 'lib/offer/offerUtils'
import DropdownSheetHeader from 'LuxKit/Dropdown/Sheet/DropdownSheetHeader'
import DropdownSheetBody from 'LuxKit/Dropdown/Sheet/DropdownSheetBody'
import { getRecommendationsTitle } from '../CalendarPicker/Recommendations/utils/recommendationsTitle'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { getDaysOtherAvailability, getPackagesForFilters, getSelectedOfferPageDay, getSelectedPackageView, isFetchingCalendars } from 'selectors/offerPage/offerPageSelectors'
import { useIntl } from 'react-intl'

const FetchingCalendarsIndicator = styled(LoadingIndicator)`
  padding: 0;
`

interface Props {
  offer: App.Offer
  airport: App.Airport
  checkIn?: string
  duration: number
  fetching?: boolean
  selectedDay?: App.CalendarDay
  flights?: boolean
  calendarError?: boolean
  calendarAvailability: boolean
  useRooms?: boolean
  defaultDayPicker?: boolean
  defaultMonth?: string
  relevantPackages: Array<App.OfferPackageOptionView>
  selectedPackageView: App.OfferPackageOptionView
  recommendationsShown: boolean
  rooms?: Array<App.Occupants>
  otherAvailability: { [checkIn: string]: boolean }
  paginator?: PaginatorState
  bundledOffersState: OfferPageState['bundledOffers']
  complementaryBundleState?: BundledOfferState
  filteredBundlePackageId?: string
  availableBundlePackages?: { [bundlePackageId: string]: BundleOfferPackageOptionView }
  isCalendarAlreadyOpen?: boolean
  recommendationsCheckInDay?: string
  recommendationsMonthIndex?: string
  recommendationsCalendarOfferIds?: Array<string>
  recommendationsCalendarState: App.ApiResponseState
}

function OfferBundleAndSaveCalendarDatePicker(props: Props) {
  const {
    checkIn,
    duration,
    offer,
    fetching,
    selectedDay,
    flights,
    calendarError,
    calendarAvailability,
    defaultMonth,
    defaultDayPicker,
    relevantPackages,
    selectedPackageView,
    otherAvailability,
    rooms,
    recommendationsShown,
    paginator,
    bundledOffersState,
    complementaryBundleState,
    filteredBundlePackageId,
    availableBundlePackages,
    isCalendarAlreadyOpen,
    recommendationsCheckInDay,
    recommendationsMonthIndex,
    recommendationsCalendarOfferIds,
    recommendationsCalendarState,
  } = props

  const intl = useIntl()
  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)
  const [monthIndex, setMonth] = useState<string | undefined>(defaultMonth ?? selectedDay?.monthKey)
  const dispatch = useOfferPageDispatch()
  const mounted = useHasMounted()

  const bundledOffers = useMemo(() => Object.values(bundledOffersState ?? {}).filter(Boolean), [bundledOffersState])

  useEffect(() => {
    if (!fetching && checkIn && selectedDay && !selectedDay.hotelAvailable && !otherAvailability[selectedDay.checkIn]) {
      // clear check in if the date we have selected is no longer available
      dispatch({ type: OfferPageActions.setCheckInDate, checkIn: undefined })
    }
  }, [checkIn, selectedDay]) // eslint-disable-line react-hooks/exhaustive-deps

  const onMonthSelect = useCallback((monthKey: string) => {
    setMonth(monthKey)
  }, [])

  const onDropdownClose = useCallback(() => {
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.CloseFilter, CALENDAR_GA_EVENT_LABEL.CloseCross)
    dispatch({ type: OfferPageActions.closeFilter })
  }, [dispatch, onUserAnalyticsAction])

  const onBackClick = useCallback(() => {
    if (recommendationsShown) {
      dispatch({ type: OfferPageActions.hideRecommendationsView })
      return
    }

    if (paginator?.backOfferId) {
      dispatch({ type: OfferPageActions.setPaginatorCurrentOfferId, currentOfferId: paginator?.backOfferId })
      return
    }

    setMonth(undefined)
  }, [setMonth, dispatch, recommendationsShown, paginator])

  const onBackToMonth = useCallback(() => {
    if (recommendationsShown) {
      dispatch({ type: OfferPageActions.hideRecommendationsView })
      return
    }
    dispatch({ type: OfferPageActions.resetDates, offerId: paginator?.currentOfferId })
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.YearView)
    setMonth(undefined)
  }, [dispatch, onUserAnalyticsAction, paginator, recommendationsShown])

  const containerRef = useRef<HTMLDivElement>(null)
  const { currentBundledOffer, currentOfferIndex } = useMemo(() => {
    const currentOfferIndex = bundledOffers.findIndex((o) => o.offer?.id === paginator?.currentOfferId)
    return {
      currentOfferIndex,
      currentBundledOffer: bundledOffers[currentOfferIndex],
    }
  }, [bundledOffers, paginator?.currentOfferId])

  const [recommendationTitle, recommendationSubtitle] = useMemo(() => {
    if (recommendationsCalendarState === 'done' && recommendationsCalendarOfferIds?.length) {
      const { title, subtitle } = getRecommendationsTitle({
        checkIn: recommendationsCheckInDay,
        monthIndex: recommendationsMonthIndex,
        duration,
        dayTitleTemplate: '{from} - {to} ({duration})',
        monthTitleTemplate: 'Check our best selling hotels available in {month}',
        defaultTitle: 'Check our best selling hotels',
      })

      return [title, subtitle] as const
    }

    return [] as const
  }, [duration, recommendationsCalendarOfferIds?.length, recommendationsCalendarState, recommendationsCheckInDay, recommendationsMonthIndex])

  const selectYourDatesLabel = intl.formatMessage({
    id: 'zo1hMc7UWF',
    defaultMessage: 'Select your dates',
    description: 'Dropdown header title for bundle calendar date picker',
  })
  const selectDatesForHotelLabel = intl.formatMessage(
    {
      id: 'NCiwgiBzrR',
      defaultMessage: 'Select dates for hotel {hotelNumber}',
      description: 'Dropdown header title showing which hotel number to select dates for in bundle booking',
    },
    { hotelNumber: currentOfferIndex + 1 },
  )
  const findingAvailabilityLabel = intl.formatMessage({
    id: 'UAhxloaeZT',
    defaultMessage: 'Finding availability',
    description: 'Loading text shown while fetching calendar availability',
  })

  const [dropdownTitle, dropdownSubtitle] = useMemo(() => {
    if (recommendationsShown) {
      return [recommendationTitle, recommendationSubtitle] as const
    }

    return [
      isBundleAndSaveV2Enabled() ? selectYourDatesLabel : selectDatesForHotelLabel,
      currentBundledOffer?.offer?.property?.name ?? currentBundledOffer?.offer?.name,
    ] as const
  }, [currentBundledOffer?.offer?.name, currentBundledOffer?.offer?.property?.name, recommendationSubtitle, recommendationTitle, recommendationsShown, selectDatesForHotelLabel, selectYourDatesLabel])

  return <>
    <DropdownSheetHeader
      size="M"
      alignment="start"
      title={dropdownTitle}
      subtitle={dropdownSubtitle}
      onDismissClick={onDropdownClose}
      onBackClick={(monthIndex !== undefined || recommendationsShown) ? onBackClick : undefined}
      actionsSlot={<Group direction="horizontal" gap={8}>
        {fetching && <BodyText variant="medium" weight="bold">{findingAvailabilityLabel}</BodyText>}
        <FetchingCalendarsIndicator inline visible={fetching} />
        {!isBundleAndSaveV2Enabled() && <OfferCalendarDurationSelect />}
        <OfferCalendarBundlePackageSelect />
      </Group>}
    />
    <DropdownSheetBody ref={containerRef}>
      <VerticalSpacer gap={16}>

        {filteredBundlePackageId && availableBundlePackages?.[filteredBundlePackageId] && <Heading variant="heading6" lineClamp={1}>
          {availableBundlePackages?.[filteredBundlePackageId].bundlePackageName}
        </Heading>}
        {recommendationsShown && <CalendarPickerRecommendations
          offerId={offer.id}
          offerType={offer.type}
          rooms={rooms}
          heading={intl.formatMessage(
            {
              id: 'Wirdt21VgD',
              defaultMessage: 'Similar hotels to {propertyName}',
              description: 'Heading for similar hotel recommendations in bundle calendar picker',
            },
            { propertyName: offer.property?.name ?? offer?.location },
          )}
          seedOffer={offer}
        />}
        {!recommendationsShown && <>
          {mounted && !fetching && !calendarAvailability && <>
            {(calendarError || !flights) && <OfferCalendarNotAvailableMessage hasError={calendarError} />}
            {!calendarError && flights && <OfferCalendarNoFlightsMessage canSwitchToHotel={!offer?.bundledWithFlightsOnly} />}
          </>}
          {calendarAvailability && <>
            {monthIndex !== undefined && !fetching && <OfferCalendarDayPicker
              onBackToMonth={onBackToMonth}
              initialMonth={monthIndex}
              defaultDayPicker={defaultDayPicker}
              flightsOnly={offer.bundledWithFlightsOnly}
              contentRef={containerRef}
            />}
            {monthIndex === undefined && <OfferCalendarMonthPicker
              onMonthSelect={onMonthSelect}
            />}
          </>}
        </>}
      </VerticalSpacer>
    </DropdownSheetBody>
    <OfferBundleAndSaveCalendarDatePickerDropdownFooter
      checkIn={checkIn}
      offer={offer}
      fetching={fetching}
      selectedDay={selectedDay}
      relevantPackages={relevantPackages}
      selectedPackageView={selectedPackageView}
      otherAvailability={otherAvailability}
      paginator={paginator}
      complementaryBundleState={complementaryBundleState}
      isCalendarAlreadyOpen={isCalendarAlreadyOpen}
    />
  </>
}

export default withOfferPageAndReduxState((currentState, appState) => {
  const state = takeCurrentOfferState(currentState)

  return {
    filteredBundlePackageId: currentState.filteredBundlePackageId,
    availableBundlePackages: getAvailableBundlePackages(currentState, appState),
    bundledOffersState: state.bundledOffers,
    paginator: state.paginator,
    checkIn: state.checkInDate,
    duration: state.duration,
    offer: state.offer,
    airport: state.airport,
    recommendationsShown: state.showRecommendationsView && shouldEnableCalendarRecommendations(state.offer),
    relevantPackages: getPackagesForFilters(appState, state),
    selectedPackageView: getSelectedPackageView(state, appState),
    rooms: state.rooms,
    otherAvailability: getDaysOtherAvailability(appState, state),
    selectedDay: getSelectedOfferPageDay(appState, state),
    fetching: isFetchingCalendars(appState, state),
    calendarAvailability: getBundleCalendarMonths(state, appState).availableCount > 0,
    calendarError: state.offer?.packages.some(pkg => appState.calendar.calendarErrors[pkg.uniqueKey]),
    complementaryBundleState: getComplementaryBundleState(currentState),
    recommendationsCheckInDay: state.recommendationsCheckInDay,
    recommendationsMonthIndex: state.recommendationsMonthIndex,
    recommendationsCalendarOfferIds: appState.recommendations.calendarRecommendations.offerIds,
    recommendationsCalendarState: appState.recommendations.calendarRecommendations.state,
  }
}, OfferBundleAndSaveCalendarDatePicker)
